import { useEffect, useState, useRef } from "react";
import { AiOutlineArrowUp } from "react-icons/ai";
import { sendAiRequest } from "api/aiChat";
import { useAiChat } from "hooks/AiChatContext";
import { getExtractedPath } from "utilities/extractedPath";
import extractUUIDs from "components/layout/utils/extractUUIDs";
import TypingIndicator from "./TypingIndicator";
import ReactMarkdown from "react-markdown";
import { FaTrash } from 'react-icons/fa';
import { FiX } from "react-icons/fi";

const MarkdownMessage = ({ text }: { text: string }) => {
  const formattedText = text.replace(/\\n/g, '\n');
  return (
    <div className="prose prose-sm text-white max-w-none">
      <ReactMarkdown>{formattedText}</ReactMarkdown>
    </div>
  );
};

const AiModelSwitch = ({
  useExternal,
  setUseExternal
}: {
  useExternal: boolean;
  setUseExternal: (value: boolean) => void;
}) => {
  return (
    <div className="flex items-center gap-2 text-white text-sm pl-2">
      <span className="opacity-70">Model:</span>
      <button
        type="button"
        onClick={() => setUseExternal(!useExternal)}
        className="opacity-90 bg-lightGray px-3 py-1 rounded-xl hover:opacity-100 transition"
      >
        {useExternal ? "External" : "Local"}
      </button>
    </div>
  );
};

const AiChat = ({ setIsMovementDisabled }: { setIsMovementDisabled: React.Dispatch<React.SetStateAction<number>> }) => {
  const baseUrl = getExtractedPath();
  const { currOrg, curSol } = extractUUIDs(baseUrl);
  const [request, setRequest] = useState<string>('');
  const [reply, setReply] = useState<string>('');
  const userId = crypto.randomUUID();
  const { setCommand, messageHistory, setMessageHistory, setAiChatShown } = useAiChat();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [useExternal, setUseExternal] = useState(false);

  const handleComand = (command: any) => {
    setCommand(command);
  };

  const handleRequestSubmit = (e: any) => {
    e.preventDefault();
    setMessageHistory((prev) => [...prev, [request, '__typing__']]);
    setRequest('');

    const sendDataToIa = async () => {
      try {
        const response = await sendAiRequest({
          user_message: request.toString(),
          user_id: userId,
          oid: currOrg,
          sid: curSol,
          agent_mode: useExternal ? 'external' : 'local',
        });

        if (response.status === 200) {
          setMessageHistory((prev) => {
            const updated = [...prev];
            const lastItem = updated[updated.length - 1];
            updated[updated.length - 1] = [lastItem[0], response.data.reply];
            return updated;
          });
          if (response.data.command) {
            handleComand(response.data.command);
          }
        } else if (response.status >= 400 && response.status < 500) {
          setMessageHistory((prev) => {
            const updated = [...prev];
            const lastItem = updated[updated.length - 1];
            updated[updated.length - 1] = [
              lastItem[0],
              'Something went wrong, try again'
            ];
            return updated;
          });
        } else if (response.status >= 500) {
          setMessageHistory((prev) => {
            const updated = [...prev];
            const lastItem = updated[updated.length - 1];
            updated[updated.length - 1] = [
              lastItem[0],
              'We ran into an issue. Wait or try again'
            ];
            return updated;
          });
        }
      } catch (error) {
        // Catch any unexpected errors (like network issues)
        console.error("AI request failed:", error);
        setMessageHistory((prev) => {
          const updated = [...prev];
          const lastItem = updated[updated.length - 1];
          updated[updated.length - 1] = [
            lastItem[0],
            'Unexpected error occurred. Please try again later'
          ];
          return updated;
        });
      }
    };

    sendDataToIa();
  };


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messageHistory]);

  const MessageHistory = () => {
    return (
      <>
        {messageHistory.map((item, index) => (
          <div className="w-full flex gap-2 flex-wrap" key={index}>
            <div className="w-full flex items-end justify-end">
              <p className="max-w-[70%] bg-[#303030] p-4 rounded-xl">{item[0]}</p>
            </div>
            <div className="w-full items-start">
              <p className="max-w-[80%] w-max p-4 bg-[#606060] rounded-xl">
                {item[1] === '__typing__' ? (
                  <TypingIndicator />
                ) : (
                  <MarkdownMessage text={item[1]} />
                )}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </>
    );
  };

  const setBlockUnity = (state: number) => {
    if (window.vuplex) {
      setIsMovementDisabled(state);
    }
  };

  return (
    <div 
      className="w-full h-full"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      onKeyPress={(e) => e.stopPropagation()}
    >
      <div className="w-full h-full flex flex-col max-h-full relative">
        <div className="flex justify-end items-center gap-2 p-3">
          <div>
            <button
              type="button"
              onClick={() => setMessageHistory([])}
              className="group flex items-center overflow-hidden transition-all duration-300 ease-in-out border-2 border-lightGray rounded-2xl px-2 py-1 w-[38px] hover:w-[150px]"
            >
              <span className="transition-opacity duration-200 group-hover:opacity-0 group-hover:absolute">
                <FaTrash />
              </span>
              <span className="whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                Clear chat history
              </span>
            </button>
          </div>
          <button
            className="cursor-pointer bg-blue rounded-md p-1 h-min"
            onClick={() => setAiChatShown(false)}
          >
            <FiX size={20} />
          </button>
        </div>
        <div className="flex-grow p-4 overflow-y-auto flex flex-col gap-2">
          <MessageHistory />
        </div>
        <div className="h-max bg-gray-300 px-4 pt-2 pb-4 min-h-24">
          <form
            className="relative h-full pb-6 bg-[#303030] outline outline-1 outline-[#424242]/70 rounded-3xl"
            onSubmit={handleRequestSubmit}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
          >
            <textarea
              onInput={(e: any) => {
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
                setRequest(e.target.value);
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                e.stopPropagation();
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleRequestSubmit(e);
                }
              }}
              onKeyUp={(e) => e.stopPropagation()}
              onKeyPress={(e) => e.stopPropagation()}
              onFocus={(e) => {
                e.stopPropagation();
                setBlockUnity(1);
                // Explicitly blur Unity canvas to release keyboard focus
                const unityCanvas = document.getElementById("unity-canvas") as HTMLCanvasElement;
                if (unityCanvas) {
                  unityCanvas.blur();
                }
                // Force focus on textarea
                e.currentTarget.focus();
              }}
              onBlur={(e) => {
                e.stopPropagation();
                setBlockUnity(0);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                // Force focus when clicking
                e.currentTarget.focus();
              }}
              tabIndex={0}
              onClick={(e) => e.stopPropagation()}
              rows={1}
              value={request}
              placeholder="Ask anything"
              className="w-full resize-none bg-transparent overflow-hidden focus:outline-none pt-2 pb-3 px-4 max-h-[200px] overflow-y-auto mb-1"
            />
            <div className="absolute bottom-1.5 w-full flex justify-end px-1.5">
              <button className="bg-white rounded-full p-1.5 disabled:opacity-50 disabled:cursor-not-allowed" type="submit">
                <AiOutlineArrowUp size={20} className="text-black" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div >
  );
};

export default AiChat;